import type { components } from "@brandex/api-client";
import { ArticlesView, BookingsView, BrandKitView, ClientScope, ProfileView, SiteStatsView, SocialView, WebsiteView, useBrandex, usePractice, websiteUrl } from "@brandex/dashboard";
import { Alert, Button, Select, Spinner, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, ShieldCheck, Users, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import { can, useStaffAccess } from "../access";
import { QueryError, errorText } from "../views";
import { manageable, useManageableClients } from "./content";

export const MANAGE_TABS = [
  { key: "profile", label: "Profile and portfolio" },
  { key: "articles", label: "Articles" },
  { key: "social", label: "Social media" },
  { key: "brand", label: "Brand kit" },
  { key: "bookings", label: "Bookings" },
  { key: "website", label: "Website" },
  { key: "visitors", label: "Visitors" },
] as const;
export type ManageTab = (typeof MANAGE_TABS)[number]["key"];

export function isManageTab(v: unknown): v is ManageTab {
  return MANAGE_TABS.some((t) => t.key === v);
}

/**
 * One client's practice, maintained by BrandEx staff with the doctor's own
 * screens: profile and portfolio, brand kit, bookings and website. The
 * server allows it for `platform.clients.manage` and audits every change;
 * approving content stays with the doctor.
 */
export function ClientManagePage({ orgId, tab, go }: { orgId: string; tab: ManageTab; go: (tab: ManageTab, org?: string) => void }) {
  const { nav, platform, siteUrl } = useBrandex();
  const access = useStaffAccess();
  const practice = usePractice(orgId);
  // Any role that edits client content, including one for assigned clients.
  const allowed = can(access.data, "platform.clients.manage", { assigned: true });
  const name = practice.data?.profile.full_name;
  const site = practice.data?.website;

  let body: ReactNode;
  if (access.isPending || practice.isPending) body = <Spinner label="Loading" />;
  else if (access.isError || practice.isError) body = <QueryError error={access.error ?? practice.error} />;
  else if (!allowed) body = <Alert tone="error">Managing a client's practice needs a role with Edit client content.</Alert>;
  else if (practice.data === null) body = <Alert tone="info">This client hasn't finished onboarding yet, so there is no practice to manage.</Alert>;
  else
    body = (
      <ClientScope orgId={orgId}>
        <TabBody tab={tab} />
      </ClientScope>
    );

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid gap-1">
          <Button size="sm" variant="ghost" className="w-fit" onClick={() => nav.navigate("/clients")}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Clients
          </Button>
          <h1 className="font-display text-2xl font-bold">{name ?? "Client"}</h1>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {allowed ? <ClientSwitcher orgId={orgId} onPick={(org) => go(tab, org)} /> : null}
          {site?.state === "live" ? (
            <Button size="sm" variant="outline" onClick={() => void platform.openExternal(websiteUrl(site, siteUrl))}>
              <ExternalLink className="h-4 w-4" aria-hidden />
              Open site
            </Button>
          ) : null}
        </div>
      </div>

      <Alert tone="info">
        <span className="flex gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            You are editing {name ?? "this client"}'s practice as BrandEx staff. Changes appear on their site straight away and are recorded in the audit log under
            your name. Only the doctor approves FAQ answers and articles.
          </span>
        </span>
      </Alert>

      {can(access.data, "platform.orgs.read") ? <AssignedStaff orgId={orgId} canAssign={can(access.data, "platform.users.manage")} /> : null}

      <nav aria-label="Practice sections" className="flex gap-1 overflow-x-auto border-b border-border">
        {MANAGE_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-current={tab === t.key ? "page" : undefined}
            onClick={() => go(t.key)}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium",
              tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {body}
    </div>
  );
}

/** The doctor's own screen for a section, editing the client in scope. */
function TabBody({ tab }: { tab: ManageTab }) {
  switch (tab) {
    case "profile":
      return <ProfileView />;
    case "articles":
      return <ArticlesView />;
    case "social":
      return <SocialView />;
    case "brand":
      return <BrandKitView />;
    case "bookings":
      return <BookingsView />;
    case "visitors":
      return <SiteStatsView />;
    case "website":
      return <WebsiteView />;
  }
}

/** Jump to another client's same section. */
function ClientSwitcher({ orgId, onPick }: { orgId: string; onPick: (orgId: string) => void }) {
  const clients = useManageableClients();
  const list = (clients.data ?? []).filter(manageable);
  if (list.length < 2) return null;
  return (
    <label className="grid gap-1 text-xs text-muted-foreground">
      Switch client
      <Select value={orgId} onChange={(e) => onPick(e.target.value)} className="min-w-56">
        {list.map((r) => (
          <option key={r.organization_id} value={r.organization_id}>{r.doctor_name ?? r.organization_name}</option>
        ))}
      </Select>
    </label>
  );
}

type Member = components["schemas"]["StaffMemberDto"];

/**
 * The staff looking after this client (ADR 0022). Account managers whose
 * roles reach only assigned clients can open a client once assigned here.
 */
export function AssignedStaff({ orgId, canAssign }: { orgId: string; canAssign: boolean }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const key = ["admin", "orgs", orgId, "staff"] as const;
  const assigned = useQuery<Member[]>({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/orgs/{org_id}/staff", { params: { path: { org_id: orgId } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const everyone = useQuery<Member[]>({
    queryKey: ["admin", "staff"],
    enabled: canAssign,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/staff");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const [pick, setPick] = useState("");
  const change = useMutation({
    mutationFn: async ({ userId, on }: { userId: string; on: boolean }) => {
      const params = { params: { path: { org_id: orgId, user_id: userId } } };
      if (on) await api.http.PUT("/v1/admin/orgs/{org_id}/staff/{user_id}", params);
      else await api.http.DELETE("/v1/admin/orgs/{org_id}/staff/{user_id}", params);
    },
    onSuccess: () => {
      setPick("");
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ["admin", "staff"] });
    },
  });
  const list = assigned.data ?? [];
  const candidates = (everyone.data ?? []).filter((m) => m.status !== "disabled" && !list.some((a) => a.id === m.id));
  return (
    <section aria-labelledby="assigned-staff" className="grid gap-2 rounded-[var(--radius-lg)] border border-border bg-card p-4">
      <h2 id="assigned-staff" className="flex items-center gap-2 text-sm font-semibold">
        <Users className="h-4 w-4" aria-hidden />
        Assigned staff
      </h2>
      {assigned.isError ? <QueryError error={assigned.error} /> : null}
      {list.length === 0 && assigned.isSuccess ? <p className="text-sm text-muted-foreground">Nobody is assigned yet.</p> : null}
      <ul className="flex flex-wrap gap-2">
        {list.map((m) => (
          <li key={m.id} className="flex items-center gap-1 rounded-[var(--radius-pill)] bg-muted py-0.5 pl-3 pr-1 text-sm">
            {m.display_name}
            {canAssign ? (
              <Button size="icon" variant="ghost" className="h-6 w-6" aria-label={`Unassign ${m.display_name}`} onClick={() => change.mutate({ userId: m.id, on: false })}>
                <X className="h-3.5 w-3.5" aria-hidden />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {canAssign && candidates.length > 0 ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (pick !== "") change.mutate({ userId: pick, on: true });
          }}
        >
          <label className="grid gap-1 text-xs text-muted-foreground">
            Assign a staff member
            <Select value={pick} onChange={(e) => setPick(e.target.value)} className="min-w-56">
              <option value="">Choose…</option>
              {candidates.map((m) => (
                <option key={m.id} value={m.id}>{m.display_name} · {m.email}</option>
              ))}
            </Select>
          </label>
          <Button type="submit" size="sm" disabled={pick === ""} loading={change.isPending}>Assign</Button>
        </form>
      ) : null}
      {change.isError ? <Alert tone="error">{errorText(change.error)}</Alert> : null}
    </section>
  );
}
